import { useEffect, useState } from 'react';
import { getPricingUsage } from '../../api/backendApi';

const ELIGIBILITY = {
  final: 'Calculated',
  beta_estimate: 'Beta estimate',
  estimate_requires_review: 'Estimate',
  not_configured: 'Not configured',
  pricing_incomplete: 'Pricing incomplete',
  not_yet_billable: 'Not yet billable',
  currency_mismatch: 'Other currency',
  unresolved: 'Unresolved',
};

export default function PricingUsagePanel() {
  const [month, setMonth] = useState('');
  const [currency, setCurrency] = useState('CAD');
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let canceled = false;
    const params = new URLSearchParams({ currency });
    if (month) params.set('month', month);
    getPricingUsage(`?${params.toString()}`)
      .then((response) => {
        if (!canceled) setReport(response?.data || null);
      })
      .catch((err) => {
        if (!canceled) setError(err?.message || 'The usage report could not be loaded.');
      });
    return () => {
      canceled = true;
    };
  }, [month, currency]);

  return (
    <section className="mt-4 rounded border border-gray-200 bg-white p-4">
      <h2 className="text-base font-medium text-gray-900">Usage and revenue</h2>
      <p className="mt-1 text-sm text-gray-600">
        Commercial totals use subscription snapshots. This is not an invoice. Twilio vendor cost is shown separately and is not dealer margin.
      </p>
      <div className="mt-3 flex gap-3">
        <label className="text-sm text-gray-700">
          Month
          <input aria-label="Usage month" className="mt-1 block rounded border border-gray-300 px-2 py-1.5" placeholder="YYYY-MM" value={month} onChange={(event) => setMonth(event.target.value)} />
        </label>
        <label className="text-sm text-gray-700">
          Currency
          <select aria-label="Usage currency" className="mt-1 block rounded border border-gray-300 px-2 py-1.5" value={currency} onChange={(event) => setCurrency(event.target.value)}>
            <option value="CAD">CAD</option>
            <option value="USD">USD</option>
          </select>
        </label>
      </div>
      {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
      {report ? (
        <div className="mt-4">
          {report.label ? <p className="text-sm font-medium text-amber-800">{report.label}. No amount is payable.</p> : null}
          <p className="text-sm text-gray-700">
            Month {report.month || 'current'} · {report.currency}
            {report.commercialBeta ? ' · Commercial beta' : ''}
          </p>
          <p className="text-sm text-gray-700">
            Estimate wholesale {report.totals?.estimateWholesale} {report.currency} · Estimate retail {report.totals?.estimateRetail} {report.currency} · Estimate margin {report.totals?.estimateMargin} {report.currency}
          </p>
          <p className="text-sm text-gray-500">
            Calculated wholesale {report.totals?.finalWholesale} {report.currency} · Calculated retail {report.totals?.finalRetail} {report.currency} · Calculated margin {report.totals?.finalMargin} {report.currency}
          </p>
          {(report.warnings || []).map((warning) => (
            <p key={warning} className="mt-1 text-sm text-amber-800">{warning}</p>
          ))}
          <ul className="mt-2 text-sm text-gray-600">
            {(report.vendorCosts || []).map((cost) => (
              <li key={cost.name}>
                {cost.name}: {cost.status === 'actual' ? `${cost.amount} ${cost.currency}` : 'Unavailable'} · {cost.period} · {cost.reason}
              </li>
            ))}
          </ul>
          {report.voiceMeasurement ? (
            <p className="mt-1 text-sm text-gray-500">
              Voice minutes {report.voiceMeasurement.minutes} (PSTN {report.voiceMeasurement.pstnMinutes}, SIP {report.voiceMeasurement.sipMinutes}, unclassified {report.voiceMeasurement.unclassifiedMinutes}). Paging excluded {report.voiceMeasurement.excludedPaging}. {report.voiceMeasurement.note}
            </p>
          ) : null}
          <table className="mt-3 w-full text-left text-sm">
            <thead className="text-gray-500">
              <tr>
                <th className="py-2 pr-3">Dealer</th>
                <th className="py-2 pr-3">Tenant</th>
                <th className="py-2 pr-3">Feature</th>
                <th className="py-2 pr-3">Quantity</th>
                <th className="py-2 pr-3">Wholesale</th>
                <th className="py-2 pr-3">Retail</th>
                <th className="py-2 pr-3">Beta</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {(report.lines || []).map((line, index) => (
                <tr key={`${line.tenantId}-${line.featureId}-${index}`}>
                  <td className="py-2 pr-3">{line.dealerId || '—'}</td>
                  <td className="py-2 pr-3">{line.tenantName || line.tenantId || '—'}</td>
                  <td className="py-2 pr-3">{line.featureName}</td>
                  <td className="py-2 pr-3">{line.quantity || '—'} {line.billingUnit || ''}</td>
                  <td className="py-2 pr-3">{line.wholesaleAmount || '—'}</td>
                  <td className="py-2 pr-3">{line.retailAmount || '—'}</td>
                  <td className="py-2 pr-3">{line.commercialBeta ? 'Beta estimate' : '—'}</td>
                  <td className="py-2">{ELIGIBILITY[line.eligibility] || line.eligibility}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
